import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { Deal, DealStage, DealsByStage } from '../../types/pipeline.types';
import { DealCard } from './DealCard';
import { Button } from '../ui/button';

interface PipelineListViewProps {
  dealStages: DealStage[];
  dealsByStage: DealsByStage;
  isLoading: boolean;
  onDealClick?: (deal: Deal) => void;
  onEditDeal?: (dealId: string) => void;
  onLoadMore?: (stageId: string) => void;
}

export function PipelineListView({
  dealStages,
  dealsByStage,
  isLoading,
  onDealClick,
  onEditDeal,
  onLoadMore,
}: PipelineListViewProps) {
  // Flatten all deals from all stages into a single list sorted by updatedAt DESC
  const allDeals = React.useMemo(() => {
    const deals: Deal[] = [];
    for (const stageId of Object.keys(dealsByStage)) {
      const stageData = dealsByStage[stageId];
      if (stageData?.deals) {
        deals.push(...stageData.deals);
      }
    }

    deals.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return dateB - dateA;
    });

    return deals;
  }, [dealsByStage]);

  // Check if any stage is still loading
  const anyLoading = React.useMemo(() => {
    if (isLoading) return true;
    for (const stageId of Object.keys(dealsByStage)) {
      if (dealsByStage[stageId]?.isLoading) return true;
    }
    return false;
  }, [isLoading, dealsByStage]);

  // Check if any stage can load more
  const hasMore = React.useMemo(() => {
    for (const stageId of Object.keys(dealsByStage)) {
      if (dealsByStage[stageId]?.canLoadMore) return true;
    }
    return false;
  }, [dealsByStage]);

  const handleLoadMore = () => {
    for (const stageId of Object.keys(dealsByStage)) {
      if (dealsByStage[stageId]?.canLoadMore) {
        onLoadMore?.(stageId);
      }
    }
  };

  if (anyLoading && allDeals.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!anyLoading && allDeals.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        No deals found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-foreground">
        {allDeals.length} Results
      </p>

      <div className="space-y-3 max-w-3xl">
        {allDeals.map((deal) => (
          <DealCard
            key={deal.id}
            deal={deal}
            onClick={onDealClick}
            onEdit={onEditDeal}
          />
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="secondary"
            onClick={handleLoadMore}
            disabled={anyLoading}
          >
            {anyLoading ? (
              <Loader2 className="size-4 animate-spin mr-2" />
            ) : null}
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
